<template>
  <v-app class="my-application">
    <Appbar></Appbar>

    <v-main>
      <PageLoader v-if="loader"></PageLoader>
      <router-view :key="$route.fullPath" :style="main"></router-view>

      <v-snackbar
        v-model="snackbar.open"
        :color="snackbar.color"
        :top="snackbar.top"
        :timeout="snackbar.timeout"
        class="mb-16"
        style="white-space: pre-line"
      >
        <span v-html="snackbar.text"> </span>
        <template v-slot:action="{ attrs }">
          <v-btn dense text icon x-small v-bind="attrs" @click="closeSnackbar">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </v-main>
  </v-app>
</template>

<script>
import PageLoader from "./components/PageLoader.vue";
import Appbar from "./components/Appbar.vue";
import { Device } from "@capacitor/device";
import { mapActions, mapMutations, mapState } from "vuex";
import { TextToSpeech } from "@capacitor-community/text-to-speech";

import { Dialog } from "@capacitor/dialog";
import { SafeArea } from "capacitor-plugin-safe-area";

import { Preferences } from "@capacitor/preferences";
import { FCM } from "@capacitor-community/fcm";
import { PushNotifications } from "@capacitor/push-notifications";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { Geolocation } from "@capacitor/geolocation";

export default {
  components: {
    Appbar,
    PageLoader,
  },

  async created() {
    try {
      if (!this.disclaimer) {
        await Dialog.alert({
          title: "Background Location",
          message:
            "Our mobile app utilizes a background geolocation feature to enhance your experience by providing location-based services. By enabling this feature, you consent to the collection and use of your device's location data as described in our Privacy Policy.",
        });

        localStorage.setItem("disclaimer", true);
      }

   
      if (this.autoPlayTTS == null) this.setAutoPlayTTS(false);
      const { platform } = await Device.getInfo();

      if (platform != "web") {
        await Geolocation.requestPermissions("location");
      }
      if (platform == "ios")
        SafeArea.getSafeAreaInsets().then(({ insets }) => {
          this.setInsets(insets);
        });

      if (platform == "android") await TextToSpeech.openInstall();

      this.loader = false;

      const sound = await Preferences.get({ key: "sound" });
      this.toggleSound(sound.value != null ? sound.value : "1");

      this.toggleSnackbar({
        open: false,
        text: null,
        color: null,
      });

      if (this.$store.state.base.mobileLang == null) {
        const code = await Device.getLanguageCode();
        const found = this.languages.find(
          (el) => el.code == code.value.split("-")[0]
        );
        console.log("found", found, "code", code);
        if (found == undefined) {
          this.setMobileLang("en");
          this.$i18n.locale = "en";
          await this.getCategories();
        } else {
          this.setMobileLang(code.value.split("-")[0]);
          this.$i18n.locale = code.value.split("-")[0];
          await this.getCategories();
        }
      } else {
        axios.defaults.headers.common[
          "Accept-Language"
        ] = `${this.mobileLang.code}`;
        this.$i18n.locale = this.mobileLang.code;
        await this.getCategories();
      }
      await this.isLanguageSupported();

      await PushNotifications.requestPermissions();

      await PushNotifications.addListener(
        "pushNotificationReceived",
        (notification) => {
          this.currentNotification = notification;
          console.log(
            "Push notification received: ",
            JSON.stringify(notification)
          );
        }
      );

      await PushNotifications.addListener(
        "pushNotificationActionPerformed",
        (notification) => {
          console.log(
            "Push notification action performed",
            notification.actionId,
            notification.inputValue
          );
        }
      );
      await PushNotifications.register();
      const { token } = await FCM.getToken();
      this.getFcm(token);
      console.log("fcm token", token);
    } catch (e) {
      console.log(e);
    }
    finally{
      this.loader = false;
    }
  },

  async mounted() {},

  data() {
    return {
      batteryLevel: null,
      eventLocation: null,
      location: null,
      loader: true,
    };
  },

  computed: {
    ...mapState({
      mobileLang: (state) => state.base.mobileLang,
      languages: (state) => state.base.languages,
      autoPlayTTS: (state) => state.base.autoPlayTTS,
      insets: (state) => state.base.insets,
      disclaimer: (state) => state.base.disclaimer,
    }),

    main() {
      return {
        padding: `${this.insets?.top}px 0px 0px 0px`,
      };
    },

    snackbar: {
      get() {
        return this.$store.state.base.snackbar;
      },
      set(value) {
        this.toggleSnackbar(value);
      },
    },
  },

  methods: {
    ...mapMutations([
      "setMobileLang",
      "toggleSnackbar",
      "setAutoPlayTTS",
      "getFcm",
      "setInsets",
    ]),
    ...mapActions([
      "getLocation",
      "toggleSound",
      "getCategories",
      "isLanguageSupported",
    ]),

    closeSnackbar() {
      this.toggleSnackbar({
        open: false,
        text: null,
        color: null,
      });
    },
  },
};
</script>

<style lang="scss">
//======VUETIFY OVERRIDES========
@import "~leaflet.markercluster/dist/MarkerCluster.css";
@import "~leaflet.markercluster/dist/MarkerCluster.Default.css";
.v-main__wrap {
  display: flex;
}

.v-toolbar__content {
  // padding: 10px !important;
  // justify-content: space-between !important;
  // flex-grow: 1;
  padding: 0px;
}

.theme--dark.v-text-field--solo > .v-input__control > .v-input__slot {
  background: var(--v-secondary-base) !important;
} //search input bg color

.v-text-field.v-text-field--solo.v-input--dense > .v-input__control {
  min-height: 25px !important;
} //search input height

.v-icon.notranslate.mdi.mdi-circle-outline.theme--light {
  color: var(--v-accent-base) !important;
} //checkbox color when unchecked

//======GLOBAL STYLES=======
.component-wrapper {
  flex-grow: 1;
  // padding-top: 10px;
}

@import url("https://fonts.googleapis.com/css2?family=Manrope:wght@200;300;400;500;600;700;800&display=swap");
$font-family: "Manrope", sans-serif;
.my-application {
  *:not(.v-icon),
  *:not(.v-icon):before,
  *:not(.v-icon):after {
    font-family: $font-family !important;
  }

  //transition styles
  .fade-enter-active,
  .fade-leave-active {
    transition: all 0.2s;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
